import { supabase } from "@/integrations/supabase/client";

/**
 * Retry policy for a SINGLE page/chunk of a multi-request fetch loop.
 *
 * Why this lives here and NOT in the global `retry` option in `src/App.tsx`:
 * TanStack Query retries the whole `queryFn`. A pager that fetched 4 pages and
 * lost page 3 would re-issue all 4 pages on retry — O(N) extra requests fired
 * into the exact request storm that caused the failure. Retrying only the
 * failed page is O(1) and keeps the N-1 pages already in hand.
 * DO NOT "simplify" this by raising the global retry instead.
 *
 * Evidence (2026-08-03 11:46:40 UTC, Kong access log + client_errors): the
 * `sale_document_items` pager got `offset=0` 200 and `offset=1000` 200, then
 * the `offset=2000` request never reached the gateway at all — the browser had
 * fired ~126 requests in the preceding 3 s against a 3.6 req/s baseline. One
 * dropped page discarded 2000 already-fetched rows and the screen rendered as
 * if empty until Ctrl+R.
 */
const MAX_PAGE_RETRIES = 2; // 3 attempts total per page
const PAGE_RETRY_BASE_DELAYS_MS = [200, 550];
// Jitter is NOT decoration. These failures arrive as a storm in which many
// pages across several concurrent pagers drop within the same few hundred ms.
// A fixed backoff would send every one of their retries back in lockstep — a
// second, correlated wave onto the same congested path, liable to fail for the
// same reason. Randomising each delay independently spreads them out.
const PAGE_RETRY_JITTER = 0.4; // +/-40% -> 120-280 ms then 330-770 ms

/**
 * True only for browser-level network failures (no HTTP response arrived).
 *
 * Two things it must NOT retry:
 *  - PostgREST/Postgres errors, which carry a `code` (`PGRST116`, `42P01`, ...).
 *    A bad filter or an RLS denial fails identically on retry.
 *  - A `TypeError` whose message we do not recognise. `TypeError` alone is not
 *    evidence of a network drop — reading a property of `undefined` inside a
 *    query builder throws one too, and retrying that hides a code bug behind
 *    added latency. The message must match as well.
 * When in doubt this returns false, so behaviour matches the pre-retry code.
 */
const NETWORK_FAILURE_MESSAGE =
  /failed to fetch|networkerror|network request failed|fetch failed|load failed|err_/i;

export function isTransientNetworkError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { code?: unknown; message?: unknown };
  if (typeof e.code === "string" && e.code.length > 0) return false;
  if (typeof e.code === "number") return false;
  const message = typeof e.message === "string" ? e.message : "";
  return NETWORK_FAILURE_MESSAGE.test(message);
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function jitteredDelayMs(attempt: number): number {
  const base = PAGE_RETRY_BASE_DELAYS_MS[attempt] ?? 550;
  const spread = base * PAGE_RETRY_JITTER;
  return Math.round(base - spread + Math.random() * spread * 2);
}

/**
 * Runs one page/chunk request, retrying ONLY that request on a transient
 * network failure. Always throws once the retries are exhausted — a partial
 * result must never be returned as if it were complete.
 */
export async function fetchPageWithRetry<T = any>(
  runPage: () => PromiseLike<{ data: T[] | null; error: any }>,
): Promise<T[]> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_PAGE_RETRIES; attempt++) {
    try {
      const { data, error } = await runPage();
      if (error) throw error;
      return (data as T[]) || [];
    } catch (err) {
      lastError = err;
      if (attempt === MAX_PAGE_RETRIES || !isTransientNetworkError(err)) throw err;
      await sleep(jitteredDelayMs(attempt));
    }
  }
  throw lastError;
}

/**
 * Fetches all rows from a Supabase query by paginating through results.
 * Supabase/PostgREST has a default limit of 1000 rows per query.
 * This function automatically pages through to get all results.
 */
export async function fetchAll<T = any>(
  buildQuery: (from: number, to: number) => any,
  pageSize = 1000
): Promise<T[]> {
  const allData: T[] = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    const data = await fetchPageWithRetry<T>(() => buildQuery(from, from + pageSize - 1));
    allData.push(...data);
    hasMore = data.length === pageSize;
    from += pageSize;
  }

  return allData;
}

/**
 * Helper to add .range() to an existing query builder.
 * Use with supabase query chains.
 */
export async function fetchAllFromQuery<T = any>(
  query: any,
  pageSize = 1000
): Promise<T[]> {
  const allData: T[] = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    // Clone the query with range - we need to rebuild each time
    const data = await fetchPageWithRetry<T>(() => query.range(from, from + pageSize - 1));
    allData.push(...data);
    hasMore = data.length === pageSize;
    from += pageSize;
  }

  return allData;
}
